/**
 * BuildService - Centralized builder orchestration and dependency management
 *
 * Responsibilities:
 * - Build calculated data from form inputs
 * - Manage dependency graph and cascade invalidation
 * - Provide single entry point for all building operations
 *
 * Usage:
 * - Pages call BuildService at save points (Continue button)
 * - Handlers never call builders directly
 * - BuildService knows all dependencies and handles cascades
 */

import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import { convertToEngineFormat as convertExistingDebt } from '~/builders/existing-debt/ExistingDebtInputs';
import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { convertToEngineFormat as convertTaxValues } from '~/builders/taxable-value/TaxableValueInputs';
import { isBondSeriesComplete } from '~/hooks/useExistingDebtHandlers';
import { useProjectStore } from '~/hooks/useProjectStore';
import type { Project, TaxableValueMapEntry } from '~/types';

/**
 * Dependency Graph (what depends on what):
 *
 * Dates (DateRecords)
 *   ↓
 *   ├─→ Tax Values (TaxableValueMap)
 *   │     ↓
 *   │     └─→ (Future: Revenue capacity)
 *   │
 *   └─→ Existing Debt (ExistingDebtProjects)
 */
type DependencyType = 'dates' | 'tax' | 'millage' | 'drf' | 'debt';

export class BuildService {
  /**
   * Build DateRecords and DateElements (with maps) from DateInputsForm
   * Cascades: Invalidates tax values and existing debt
   */
  static buildDateRecords(projectId: string): boolean {
    const store = useProjectStore.getState();
    const project = store.projects[projectId];

    if (!project?.dateInputs) {
      console.warn('[BuildService] Cannot build DateRecords: no dateInputs found');
      return false;
    }

    try {
      // Validate inputs
      const validation = DateBuilder.validateInputs(project.dateInputs);
      if (!validation.isValid) {
        console.warn('[BuildService] DateInputs validation failed:', validation.errors);
        return false;
      }

      // Convert and build date records array
      const engineInputs = DateBuilder.convertToEngineFormat(project.dateInputs);
      const dateRecords = DateBuilder.buildDateRecords(engineInputs);

      // Save date records to store first
      store.saveDateRecords(projectId, dateRecords);

      // Now build the date maps (dateElements) using the saved records
      // Create a temporary Project object for buildBaseProjectElements
      const updatedProject = store.projects[projectId];
      const projectForMaps: Project = {
        id: updatedProject.metadata.id,
        name: updatedProject.metadata.name,
        dateInputs: updatedProject.dateInputs,
        taxValueInputs: updatedProject.taxValueInputs,
        existingDebtInputs: updatedProject.existingDebtInputs,
        millageInputs: updatedProject.millageInputs,
        drfInputs: updatedProject.drfInputs,
        taxableValueMap: updatedProject.taxableValueMap,
        existingDebtProjects: updatedProject.existingDebtProjects,
        dateElements: {
          dateRecords,
          dateRecordsMapByTaxYear: undefined,
          dateRecordsMapByPaymentDate: undefined,
        },
        configurations: updatedProject.configurations,
        lastUpdated: updatedProject.metadata.updatedAt,
        processingStatus: 'complete',
      };

      const dateElements = DateBuilder.buildBaseProjectElements(projectForMaps);

      // Save the complete dateElements (with maps) to store
      store.saveDateElements(projectId, dateElements);

      // Cascade invalidation
      BuildService.invalidateDependents(projectId, 'dates');

      return true;
    } catch (error) {
      console.error('[BuildService] Error building DateRecords:', error);
      return false;
    }
  }

  /**
   * Build TaxableValueMap from TaxValueInputsForm
   * Requires: DateRecords
   * Cascades: None currently (future: revenue capacity)
   */
  static buildTaxableValueMap(projectId: string): boolean {
    const store = useProjectStore.getState();
    const project = store.projects[projectId];

    if (!project?.taxValueInputs) {
      console.warn('[BuildService] Cannot build TaxableValueMap: no taxValueInputs found');
      return false;
    }

    if (!project?.dateRecords || project.dateRecords.length === 0) {
      console.warn('[BuildService] Cannot build TaxableValueMap: dateRecords required');
      return false;
    }

    try {
      // Convert form to engine format
      const engineInputs = convertTaxValues(project.taxValueInputs);

      // Validate inputs
      const validation = TaxableValueBuilder.validateInputs(engineInputs);
      if (!validation.isValid) {
        console.warn('[BuildService] TaxValueInputs validation failed:', validation.errors);
        return false;
      }

      // Build with dateRecords context
      // Calculate years from dateRecords length (each record represents one year)
      const startingTaxYear = parseInt(project.dateInputs?.startingTaxYear || '', 10);
      const years = project.dateRecords.length;

      const taxableValueArray = TaxableValueBuilder.buildWithFixedYears(
        engineInputs,
        startingTaxYear,
        years
      );

      // Convert array to map keyed by tax year
      // The builder returns an array, but we need an object keyed by year for engine compatibility
      const taxableValueMap = taxableValueArray.reduce(
        (map, entry) => {
          map[entry.taxYear] = entry;
          return map;
        },
        {} as Record<number, TaxableValueMapEntry>
      );

      // Save to store
      store.saveTaxableValueMap(projectId, taxableValueMap);

      // No cascades currently (future: revenue capacity)

      return true;
    } catch (error) {
      console.error('[BuildService] Error building TaxableValueMap:', error);
      return false;
    }
  }

  /**
   * Build ExistingDebtProjects from ExistingDebtInputsForm
   * Requires: DateRecords
   * Cascades: None
   */
  static buildExistingDebtProjects(projectId: string): boolean {
    const store = useProjectStore.getState();
    const project = store.projects[projectId];

    if (!project?.existingDebtInputs || project.existingDebtInputs.length === 0) {
      console.warn('[BuildService] Cannot build ExistingDebtProjects: no existingDebtInputs found');
      return false;
    }

    if (!project?.dateRecords || project.dateRecords.length === 0) {
      console.warn('[BuildService] Cannot build ExistingDebtProjects: dateRecords required');
      return false;
    }

    try {
      // Filter to complete projects only
      const completeProjects = project.existingDebtInputs.filter((form) => {
        if (
          !form.id?.trim() ||
          !form.name?.trim() ||
          !form.payments ||
          form.payments.length === 0
        ) {
          return false;
        }
        // Check if bond series is complete (last two principal payments > 0)
        return isBondSeriesComplete(form);
      });

      if (completeProjects.length === 0) {
        console.warn('[BuildService] No complete debt projects to build');
        store.clearExistingDebtProjects(projectId);
        return false;
      }

      // Convert and build each project
      const processed = completeProjects.map((form) => {
        const engineFormat = convertExistingDebt(form);
        return ExistingDebtBuilder.processDebtProject(engineFormat, project.dateRecords!);
      });

      // Save to store
      store.saveExistingDebtProjects(projectId, processed);

      return true;
    } catch (error) {
      console.error('[BuildService] Error building ExistingDebtProjects:', error);
      return false;
    }
  }

  /**
   * Build all calculated data for a project
   * Useful at page boundaries (Continue button)
   * Respects dependency order
   */
  static buildAllForProject(projectId: string): {
    dates: boolean;
    tax: boolean;
    debt: boolean;
  } {
    const results = {
      dates: false,
      tax: false,
      debt: false,
    };

    // Build in dependency order
    results.dates = BuildService.buildDateRecords(projectId);

    if (results.dates) {
      results.tax = BuildService.buildTaxableValueMap(projectId);
      results.debt = BuildService.buildExistingDebtProjects(projectId);
    }

    return results;
  }

  /**
   * Invalidate dependents when a data type changes
   * Centralized cascade invalidation
   */
  private static invalidateDependents(projectId: string, changed: DependencyType): void {
    const store = useProjectStore.getState();
    const project = store.projects[projectId];

    if (!project) return;

    switch (changed) {
      case 'dates':
        // DateRecords changed → clear everything that depends on dates
        if (project.taxableValueMap) {
          store.clearTaxableValueMap(projectId);
        }
        if (project.existingDebtProjects) {
          store.clearExistingDebtProjects(projectId);
        }
        break;

      case 'tax':
        // TaxableValueMap changed → clear everything that depends on tax values
        // (Future: revenue capacity calculations)
        break;

      case 'millage':
        // Millage changed → clear millage-dependent calculations
        // (Future: millage shape calculations)
        break;

      case 'drf':
        // DRF changed → no cascades currently
        break;

      case 'debt':
        // Existing debt changed → no cascades currently
        break;
    }
  }

  /**
   * Check if a project has all dependencies needed to build a specific data type
   */
  static canBuild(
    projectId: string,
    type: DependencyType
  ): {
    canBuild: boolean;
    reason?: string;
  } {
    const store = useProjectStore.getState();
    const project = store.projects[projectId];

    if (!project) {
      return { canBuild: false, reason: 'Project not found' };
    }

    switch (type) {
      case 'dates':
        if (!project.dateInputs) {
          return { canBuild: false, reason: 'No date inputs' };
        }
        return { canBuild: true };

      case 'tax':
        if (!project.taxValueInputs) {
          return { canBuild: false, reason: 'No tax value inputs' };
        }
        if (!project.dateRecords || project.dateRecords.length === 0) {
          return { canBuild: false, reason: 'DateRecords required (build dates first)' };
        }
        return { canBuild: true };

      case 'debt':
        if (!project.existingDebtInputs || project.existingDebtInputs.length === 0) {
          return { canBuild: false, reason: 'No existing debt inputs' };
        }
        if (!project.dateRecords || project.dateRecords.length === 0) {
          return { canBuild: false, reason: 'DateRecords required (build dates first)' };
        }
        return { canBuild: true };

      case 'millage':
      case 'drf':
        // These don't build calculated data currently
        return { canBuild: false, reason: 'No building needed (configuration only)' };

      default:
        return { canBuild: false, reason: 'Unknown type' };
    }
  }
}
